import { OrganizationUserType } from '@mfw/common';
import { useIntl } from '@mfw/components';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Skeleton from '@mui/material/Skeleton';
import React from 'react';

import {
  useOrganizationContext,
  usePermissionsContext,
  useNavigationContext,
  useAuthContext,
  useActingUserType,
} from '../../../context';
import { NavigationItem, ListSubheader, List } from '../../../components';
import useOrgDisplayName from '../../../hooks/organizations/useOrgDisplayName';
// By module path: the `hooks/tenants` barrel carries every tenant API hook.
import useTenantFeatures from '../../../hooks/tenants/useTenantFeatures';
import { useBuildRoute } from '../../../router/hooks';
import { TenantSwitcher } from '../../TenantSwitcher/TenantSwitcher';
import SidebarPlatformAdminSection from './SidebarPlatformAdminSection/SidebarPlatformAdminSection';
import SidebarSection from './SidebarSection/SidebarSection';
import SidebarTopItems from './SidebarTopItems/SidebarTopItems';
import { useOrgSidebarSections } from './useOrgSidebarSections';
import messages from './messages';

/**
 * Custom scrollbar for the nav list — targets the inner `Scrollbar` div
 * (see `components/Scrollbar`), which is what actually scrolls.
 */
const scrollbarSx = {
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 2,
  // Without this a flex item's default min-height (`auto`) sizes it to fit
  // ALL of its content, so `overflowY: auto` never actually kicks in — the
  // item just grows past the sidebar's bounded height instead of scrolling,
  // taking the footer down with it.
  minHeight: 0,
  overflowY: 'auto',
  px: 1,
  '& ::-webkit-scrollbar': {
    scrollbarWidth: 'thin',
    width: '5px',
  },
  '& ::-webkit-scrollbar-thumb': {
    backgroundColor: 'grey.500',
    borderRadius: '10px',
  },
  '& ::-webkit-scrollbar-track': {
    backgroundColor: 'grey.700',
  },
} as const;

export interface SidebarContentProps {
  children?: React.ReactNode;
  className?: string;
}
const SidebarContent = ({ children, className }: SidebarContentProps) => {
  const { currentUser } = useAuthContext();
  const { hasPermission } = usePermissionsContext();
  const { isOn } = useTenantFeatures();
  const { formatMessage } = useIntl();
  const { currentOrganization, isCorpMode, isLoading } =
    useOrganizationContext();
  const { of } = useBuildRoute();

  const { isMiniBarAvailable, isMobile, setNavigationOpen } =
    useNavigationContext();
  const actingUserType = useActingUserType();
  // Marketplace renders here, at the very bottom after every org section, ONLY for OWNER/
  // TRAINER — CLIENT, STAFF and platform users keep it up in
  // `SidebarTopItems` instead (see the comments there).
  const isOwnerOrTrainer =
    !currentUser?.isPlatformUser &&
    actingUserType !== OrganizationUserType.CLIENT &&
    actingUserType !== OrganizationUserType.STAFF;

  const handleRouteChange = React.useCallback(() => {
    if (isMobile) setNavigationOpen(false);
  }, [isMobile, setNavigationOpen]);

  // Called unconditionally (Rules of Hooks) even though its result is only
  // rendered below the `isCorpMode` early return — corp mode has no org
  // sections to show.
  const { isOrgAdminView, sections } = useOrgSidebarSections();
  // Same fallback as the "<Org> Home" / "<Org> Schedule" rows beneath it —
  // the raw `name` is `MaybeString` and rendered an empty `()`.
  const orgDisplayName = useOrgDisplayName();

  // In corp mode the user is not inside a regular organization context, so we
  // show a restricted navigation limited to the corp-facing pages.
  if (isCorpMode) {
    return (
      <Box className={className} sx={scrollbarSx}>
        <List>
          {!isMiniBarAvailable && (
            <ListSubheader>{formatMessage(messages.dashboard)}</ListSubheader>
          )}
          <NavigationItem
            label={formatMessage(messages.home)}
            onClick={handleRouteChange}
            data-testid="sidebar-home"
            mini={isMiniBarAvailable}
            icon="mdi:house"
            to={of('/')}
          />
          {/* Same glyph as the personal "My calendar" row in the org
              sidebar: one route (`/schedule`), one icon, in every mode. */}
          <NavigationItem
            label={formatMessage(messages.myCalendar)}
            onClick={handleRouteChange}
            data-testid="sidebar-schedule"
            mini={isMiniBarAvailable}
            icon="mdi:calendar-account"
            to={of('/schedule')}
          />
          <NavigationItem
            label={formatMessage(messages.saved)}
            onClick={handleRouteChange}
            icon="mdi:bookmark-outline"
            data-testid="sidebar-saved"
            mini={isMiniBarAvailable}
            to={of('/saved')}
          />
        </List>
        {children}
      </Box>
    );
  }

  // Until the selected organization is known, every row below would be drawn
  // for the personal profile and then swapped for the organization's — the
  // menu flashed the client items on login. Placeholders instead.
  if (isLoading) {
    return (
      <Box className={className} sx={scrollbarSx} data-testid="sidebar-loading">
        <List>
          {Array.from({ length: 6 }, (_, index) => (
            <Skeleton
              key={index}
              variant="rounded"
              height={40}
              sx={{ mx: 1, my: 0.5 }}
            />
          ))}
        </List>
        {children}
      </Box>
    );
  }

  return (
    <Box className={className} sx={scrollbarSx}>
      <List>
        <SidebarTopItems onNavigate={handleRouteChange} />
        {/* Platform users are management-first: the admin block sits above the
            org drill-down instead of below every org section. */}
        {/* Super admin only (the component gates itself): picking a tenant
            makes the whole app act as that tenant's admin. A select does not
            fit the mini bar — expand the sidebar to switch. */}
        {!isMiniBarAvailable && (
          <Box px={1} pb={1}>
            <TenantSwitcher />
          </Box>
        )}
        <SidebarPlatformAdminSection onNavigate={handleRouteChange} />
        {currentOrganization?.id && (
          <>
            {/* Platform-user drill-down: a rule sets the org sections apart
                from the admin block above, in the mini bar too. */}
            {currentUser?.isPlatformUser && <Divider sx={{ mx: 1, my: 1 }} />}
            {/* They hold no membership in this org (P1), so this caption is
                the only indicator of which org's sections they're looking
                at. */}
            {currentUser?.isPlatformUser &&
              !isMiniBarAvailable &&
              hasPermission('organizations_read') && (
                <ListSubheader>
                  <>
                    {formatMessage(messages.currentOrganization)}&nbsp;(
                    {orgDisplayName})
                  </>
                </ListSubheader>
              )}
            {sections.map((section) => (
              <SidebarSection
                key={section.id}
                id={section.id}
                label={section.label}
                items={section.items}
                persistKey={`${currentUser?.id ?? 'anonymous'}:${
                  isOrgAdminView ? 'admin' : 'trainer'
                }:${section.id}`}
                isMiniBarAvailable={isMiniBarAvailable}
                onNavigate={handleRouteChange}
                soonLabel={formatMessage(messages.soon)}
              />
            ))}
            {/* Marketplace is a standalone row below every section, no
                section header of its own. It is discovery of other people's
                listings, so the tenant's marketplace switch takes it away. */}
            {isOwnerOrTrainer && isOn('marketplace') && (
              <NavigationItem
                label={formatMessage(messages.marketplace)}
                onClick={handleRouteChange}
                data-testid="sidebar-marketplace"
                mini={isMiniBarAvailable}
                icon="mdi:storefront-outline"
                to={of('/marketplace')}
              />
            )}
          </>
        )}
      </List>
      {children}
    </Box>
  );
};

export default SidebarContent;
